/**
 * Task Detail Drawer Component
 * 
 * Side panel showing detailed task information
 */

import { UnifiedTask, TaskStatus } from '@/types/tasks';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Calendar,
  User,
  ExternalLink,
  CheckCircle2,
  Clock,
  PoundSterling,
  Tag,
  Building2,
  FileText,
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface TaskDetailDrawerProps {
  task: UnifiedTask | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChange?: (taskId: string, newStatus: TaskStatus) => void;
}

const priorityColors = {
  Critical: 'bg-red-500 text-white',
  High: 'bg-orange-500 text-white',
  Medium: 'bg-blue-500 text-white',
  Low: 'bg-gray-500 text-white',
};

const statusLabels = {
  todo: 'To Do',
  in_progress: 'In Progress',
  blocked: 'Blocked',
  completed: 'Completed',
};

export function TaskDetailDrawer({ task, open, onOpenChange, onStatusChange }: TaskDetailDrawerProps) {
  const navigate = useNavigate();

  if (!task) return null;

  const handleNavigate = () => {
    navigate(task.sourceUrl);
    onOpenChange(false);
  };

  const handleStatusChange = (newStatus: TaskStatus) => {
    onStatusChange?.(task.id, newStatus);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-xl">{task.title}</SheetTitle>
              <SheetDescription className="mt-2">
                {task.description}
              </SheetDescription>
            </div>
            <Badge className={cn("shrink-0", priorityColors[task.priority])}>
              {task.priority}
            </Badge>
          </div>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Status */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Status</h4>
            <div className="flex gap-2 flex-wrap">
              {(['todo', 'in_progress', 'blocked', 'completed'] as TaskStatus[]).map(status => (
                <Button
                  key={status}
                  size="sm"
                  variant={task.status === status ? 'default' : 'outline'}
                  onClick={() => handleStatusChange(status)}
                >
                  {statusLabels[status]}
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          {/* Details */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Details</h4>
            
            {/* Customer */}
            <div className="flex items-center gap-3">
              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{task.customerName}</p>
                <p className="text-xs text-muted-foreground">Customer ID: {task.customerId}</p>
              </div>
            </div>

            {/* Amount */}
            {task.amount && (
              <div className="flex items-center gap-3">
                <PoundSterling className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium">£{task.amount.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Amount</p>
                </div>
              </div>
            )}

            {/* Due Date */}
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">{format(task.dueDate, 'PPP')}</p>
                <p className="text-xs text-muted-foreground">Due Date</p>
              </div>
            </div>

            {/* Assigned To */}
            <div className="flex items-center gap-3">
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">{task.assignedTo}</p>
                <p className="text-xs text-muted-foreground">Assigned To</p>
              </div>
            </div>

            {/* Source */}
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">{task.sourceType}</p>
                <p className="text-xs text-muted-foreground">Source: {task.sourceId}</p>
              </div>
            </div>

            {/* Created/Updated */}
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">{format(task.createdAt, 'PPP')}</p>
                <p className="text-xs text-muted-foreground">
                  Created • Updated {format(task.updatedAt, 'PPP')}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Tags */}
          {task.tags.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Tags
              </h4>
              <div className="flex flex-wrap gap-2">
                {task.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <Separator />

          {/* Actions */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Actions</h4>
            
            <Button
              className="w-full"
              onClick={handleNavigate}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Full Details
            </Button>

            {task.status !== 'completed' && (
              <Button
                className="w-full"
                variant="outline"
                onClick={() => handleStatusChange('completed')}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark as Complete
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
